import React, {Dispatch, useCallback, useEffect, useMemo, useReducer, useRef} from "react";
import _ from "lodash";
import {addDays, addSeconds, endOfDay, isAfter, isBefore, isValid, startOfDay} from 'date-fns';
import { DateUtils, Interval } from "../../../core/utils/dateUtils";
import { createServiceProvider } from "../../../context/AppServiceProvider";
import { RoundScheduleItem, RoundUtils, RoundUtilStores } from "../../../syncstream/utils/RoundUtils";
import { useSyncCenter } from "../../../syncstream/SyncCenterProvider";
import { useMap } from "../../../core/storage/hooks/UseStore";
import {Logger} from "../../../core/logger/logger";

interface Parameters {
    // Stores used to calculate result
    stores: RoundUtilStores;
    interval: Interval;
    facilityIds: number[];
    includeAdministered: boolean;
    notBefore: Date | undefined;
}
const logger = new Logger('GetSchedule');

interface NeedNewRoundScheduleProps {
    cachedParameters?: Parameters | null;
    requestedParameters: Parameters;
}
function needNewRoundSchedule(props: NeedNewRoundScheduleProps): boolean {
    const {cachedParameters, requestedParameters} = props;
    if (!cachedParameters) {
        return true;
    }
    if (!!cachedParameters.notBefore && new Date() < cachedParameters.notBefore) {
        // Too soon.  Ignore.
        logger.info("Deferring re-calculation of cache until ", cachedParameters.notBefore);
        return false;
    }
    if (!_.isEqual(cachedParameters.stores, requestedParameters.stores) ||
        !_.isEqual(cachedParameters.facilityIds, requestedParameters.facilityIds)||
        !_.isEqual(cachedParameters.includeAdministered, requestedParameters.includeAdministered)) {
        return true;
    }

    const cacheStart = DateUtils.toDate(cachedParameters.interval.start);
    const cacheEnd = DateUtils.toDate(cachedParameters.interval.end);
    const requestedStart = DateUtils.toDate(requestedParameters.interval.start);
    const requestedEnd = DateUtils.toDate(requestedParameters.interval.end);

    return isBefore(requestedStart, cacheStart) || isAfter(requestedEnd, cacheEnd);
}

// This function was no longer used because of the Cinderella bug where before midnight it would
// include the following day's data
// eslint-disable-next-line sonarjs/cognitive-complexity
function areParametersEqual(p1: Parameters | null, p2 : Parameters | null) {
    if (!p1 || !p2) {
        return false;
    }
    if (_.isEqual(p1, p2)) {
        return true;
    }

    // This helps with testing to verify what exactly has changed.
    // At some point it could be removed.

    /****

    if (!_.isEqual(p1.interval, p2.interval)) {
        console.log("GetSchedule: interval has changed");
    }
    if (!_.isEqual(p1.facilityIds, p2.facilityIds)) {
        console.log("GetSchedule: facilities have changed");
    }
    if (!_.isEqual(p1.stores, p2.stores)) {
        if (p1.stores.drugStore !== p2.stores.drugStore) {
            console.log("GetSchedule: drugs have changed");
        }
        if (p1.stores.facilityStore !== p2.stores.facilityStore) {
            console.log("GetSchedule: facilities have changed");
        }
        if (p1.stores.roundStore !== p2.stores.roundStore) {
            console.log("GetSchedule: rounds have changed");
        }
        if (p1.stores.patientStore !== p2.stores.patientStore) {
            console.log("GetSchedule: patients have changed");
        }
        if (p1.stores.packedDayStore !== p2.stores.packedDayStore) {
            console.log("GetSchedule: packed days have changed");
        }
        if (p1.stores.facilityGroupConfigurationStore !== p2.stores.facilityGroupConfigurationStore) {
            console.log("GetSchedule: facility group configurations have changed");
        }
        if (p1.stores.packedPrnStore !== p2.stores.packedPrnStore) {
            console.log("GetSchedule: packed prns have changed");
        }
        if (p1.stores.patchObservationStore !== p2.stores.patchObservationStore) {
            console.log("GetSchedule: patch observations have changed");
        }
        if (p1.stores.syringeDriverActivityStore !== p2.stores.syringeDriverActivityStore) {
            console.log("GetSchedule: syringe driver activities have changed");
        }
    }

    ***/

    return false;
}

interface State {
    parameters: Parameters | null;
    result: RoundScheduleItem[];
}

const initialState: State = {
    parameters: null,
    result: [],
};

type ContextValue = [State, Dispatch<Action>];

const [Provider, useHook] = createServiceProvider<ContextValue>();

interface ReplaceStateAction {
    type: "REPLACE" | "DEFER" | "CLEAR_DEFERRAL";
    payload: State;
}

type Action = ReplaceStateAction;

function reducer(state: State, action: Action): State {
    if (action.type === "REPLACE") {
        state = action.payload;
    }
    else if (action.type === "DEFER") {
        state = action.payload;
    }
    else if (action.type === "CLEAR_DEFERRAL" && !!state.parameters?.notBefore) {
        state = action.payload;
    }
    return state;
}

interface GetScheduleProviderProps {
    children: React.ReactNode;
}
export function GetScheduleProvider(props: GetScheduleProviderProps) {
    const [state, rawDispatch] = useReducer(reducer, initialState);
    // getScheduleForDay defers its dispatch via setTimeout with no cleanup; guard against
    // dispatching into an unmounted reducer to avoid React's "state update on unmounted component" warning.
    const mountedRef = useRef(true);
    useEffect(() => () => { mountedRef.current = false; }, []);
    const dispatch = useCallback<Dispatch<Action>>((action) => {
        if (mountedRef.current) {
            rawDispatch(action);
        }
    }, []);
    const value = useMemo<ContextValue>(() => [state, dispatch], [state, dispatch]);
    const {children} = props;
    useEffect(() => {
        const now = new Date();
        const notBefore = value[0].parameters?.notBefore;
        if (notBefore && notBefore > now) {
            const ms = notBefore.getTime() - now.getTime();
            const timerId = setTimeout(() => {
                value[1]({type : "CLEAR_DEFERRAL", payload: value[0]});
            }, ms);
            return () => clearTimeout(timerId);
        }
    }, [value]);
    return (
        <Provider value={value}>
            {children}
        </Provider>
    );
}

function useRoundUtilStore(): RoundUtilStores {
    const services = useSyncCenter();

    const packedDayStore = useMap(services.packedPatientDays);
    const patientStore = useMap(services.patients);
    const drugStore = useMap(services.drugs);
    const packedPrnStore = useMap(services.packedPatientPrnMedications);
    const roundStore = useMap(services.rounds);
    const facilityStore = useMap(services.facilities);
    const facilityGroupConfigurationStore = useMap(services.facilityGroupConfigurations);
    const syringeDriverRunStore = useMap(services.syringeDriverRun);
    const patchApplicationStore = useMap(services.patchApplications);

    return useMemo(() => ({
        packedDayStore,
        patientStore,
        drugStore,
        packedPrnStore,
        roundStore,
        facilityStore,
        facilityGroupConfigurationStore,
        syringeDriverRunStore,
        patchApplicationStore
    }), [
        packedDayStore,
        patientStore,
        drugStore,
        packedPrnStore,
        roundStore,
        facilityStore,
        facilityGroupConfigurationStore,
        syringeDriverRunStore,
        patchApplicationStore
    ]);
}

// This returns a function that can be called from within a side-effect or a render method
export function useGetScheduleFromParameters() {
    const contextValue = useHook();
    const stores = useRoundUtilStore();
    return useCallback((interval: Interval, facilityIds: number[], includeAdministered: boolean) => {
        return getScheduleFromParameters(interval, facilityIds, stores, contextValue, includeAdministered);
    }, [contextValue, stores]);
}
export function useDelaySchedule() {
    const contextValue = useHook();
    return useCallback((delayInSeconds: number) => {
        const [state, dispatch] = contextValue;
        const newState = {...state}
        if (!newState.parameters) {
            return;
        }
        if (!delayInSeconds) {
            newState.parameters.notBefore = undefined;
            dispatch({type: "CLEAR_DEFERRAL", payload: newState});        
        }
        else {
            const newTm = addSeconds(new Date(), delayInSeconds);
            if (!newState.parameters.notBefore || newState.parameters.notBefore < newTm) {
                newState.parameters.notBefore = newTm;
                dispatch({type: "DEFER", payload: newState});
            }
        }
    }, [contextValue]);
    
}

function getScheduleFromParameters(interval: Interval, facilityIds: number[], stores: RoundUtilStores, contextValue: ContextValue, includeAdministered: boolean): RoundScheduleItem[] {
    const dayParameters = getParametersForDay(interval, facilityIds, stores, includeAdministered, contextValue[0].parameters?.notBefore ?? undefined);
    const dayItems = getScheduleForDay(dayParameters, contextValue);
    return filterItems(dayItems, interval);
}

function filterItems(items: RoundScheduleItem[], interval: Interval): RoundScheduleItem[] {
    const filteredItems: RoundScheduleItem[] = [];
    const dateInterval = DateUtils.intervalToDateInterval(interval);

    for (const item of items) {
        const timestamp = getTimestamp(item);
        if (isBefore(timestamp, dateInterval.start)) {
            continue;
        }
        if (isAfter(timestamp, dateInterval.end)) {
            continue;
        }
        filteredItems.push(item);
    }

    return filteredItems;
}

function getTimestamp(item: RoundScheduleItem): Date {
    return DateUtils.toDate(item.packedMedication.doseTimestamp!);
}

function getParametersForDay(interval: Interval, facilityIds: number[], stores: RoundUtilStores, includeAdministered: boolean, notBefore: Date | undefined): Parameters {
    const dateInterval = DateUtils.intervalToDateInterval(interval);
    dateInterval.start = startOfDay(dateInterval.start);
    // now we are getting tomorrow's data as well
    dateInterval.end = endOfDay(dateInterval.end);
      // if (Math.random() > 0.5) {
      //     console.log("Adding an Extra Day");
      //     dateInterval.end = addDays(dateInterval.end, 1);
      // }

    const dayInterval: Interval = {
        start: DateUtils.fromDate(dateInterval.start),
        end: DateUtils.fromDate(dateInterval.end)
    };

    facilityIds = [...facilityIds];
    facilityIds.sort((id1, id2) => id1 - id2);

    stores = { ...stores };

    return {
        stores,
        facilityIds,
        interval: dayInterval,
        includeAdministered: includeAdministered, 
        notBefore: notBefore
    };
}

// getScheduleForDay gets called multiple times concurrently.  This tempState is a rather pathetic
// attempt to stop this recalculating.
let tempState: State | null = null;
function getScheduleForDay(parameters: Parameters, contextValue: ContextValue) {
    const [state, dispatch] = contextValue;

    if (!needNewRoundSchedule({cachedParameters: state.parameters, requestedParameters: parameters})) {
        return state.result;
    }

    if (tempState && !needNewRoundSchedule({cachedParameters: tempState!.parameters, requestedParameters: parameters})) {
        return tempState!.result;
    }
    logger.info("Recomputing daily schedule for range " + parameters.interval.start + " to " + parameters.interval.end);
    const roundUtils = new RoundUtils(parameters.stores);
    const result = roundUtils.getScheduleFromParameters(parameters.interval, parameters.facilityIds, parameters.includeAdministered);
    tempState = {
        parameters: {...parameters},
        result: [...result], 
    }
    const newState = {
        parameters: {...parameters},
        result: [...result]
    };
    setTimeout(() => dispatch({type: "REPLACE", payload: newState}));
    setTimeout(() => tempState = null);
    logger.info("Daily schedule recomputed");
    return result;
}
